import { eq, gte } from 'tio-ui/utilities';
import { hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { t } from 'ember-intl';
import type { TemplateOnlyComponent } from '@ember/component/template-only';
import { type TBodyOptions } from 'tio-reimagined/components/table';
import Component from '@glimmer/component';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import safeT from 'tio-common/helpers/safe-t';
import type LoanModel from 'tio-common/models/loan';
import { PhWarning } from 'ember-phosphor-icons';
import type { ObservableSource } from 'tio-employee/services/observability';

export interface AccountsLinkedLoansSummaryLoanDetailSignature {
  Args: {
    loan: LoanModel;
    style: 'row' | 'list';
    options?: typeof TBodyOptions;
    source: ObservableSource;
  };
}

interface RepaymentPlanWarningSignature {
  Args: {
    source: ObservableSource;
  };
}

const RepaymentPlanWarning: TemplateOnlyComponent<RepaymentPlanWarningSignature> = <template>
  <div class="text-red-500 flex items-center gap-2">
    <PhWarning @size="16" />
    <LinkTo
      @route="authenticated.observability.upload"
      @query={{hash source=@source providers="nslds"}}
      class="text-red-500 underline hover:text-red-700"
    >
      {{t "pslf_dashboard.actions.dialog.upload_loan_doc_to_add_plan"}}
    </LinkTo>
  </div>
</template>;

export default class AccountsLinkedLoansSummaryLoanDetail extends Component<AccountsLinkedLoansSummaryLoanDetailSignature> {
  get isMissingRepaymentPlan() {
    return (
      !this.args.loan.repaymentPlan ||
      this.args.loan.repaymentPlan === 'RepaymentPlan.UNKNOWN' ||
      this.args.loan.repaymentPlan === 'RepaymentPlan.NONE'
    );
  }

  <template>
    {{#if (eq @style "row")}}
      <@options.tr class="align-top">
        <@options.td>{{@loan.name}}</@options.td>
        <@options.td>
          {{#if this.isMissingRepaymentPlan}}
            <RepaymentPlanWarning @source={{@source}} />
          {{else}}
            {{safeT @loan.repaymentPlan nestedPath="common.repayment_plans" defaultToBlank=true}}
          {{/if}}
        </@options.td>
        <@options.td>
          {{@loan.loanTypeDisplayName}}
        </@options.td>
        <@options.td>
          {{@loan.interestRate}}
        </@options.td>
        <@options.td class="p-2 text-right">
          {{#if (gte @loan.currentBalance 0)}}
            {{safeFormatNumber @loan.currentBalance style="currency" currency="USD"}}
          {{/if}}
        </@options.td>
        <@options.td />
      </@options.tr>
    {{else if (eq @style "list")}}
      <li class="border border-gray-200 rounded-md p-2 bg-white mb-2">
        <div class="grid grid-cols-7 auto-rows-min gap-1 items-start relative">
          <h2 class="col-span-3 text-right font-semibold">
            {{t "pslf_dashboard.actions.dialog.loan_name"}}:
          </h2>
          <p class="col-span-4 truncate">
            {{@loan.name}}
          </p>

          <h2 class="col-span-3 text-right font-semibold">
            {{t "pslf_dashboard.actions.dialog.repayment_plan"}}:
          </h2>
          <p class="col-span-4">
            {{#if this.isMissingRepaymentPlan}}
              <RepaymentPlanWarning @source={{@source}} />
            {{else}}
              {{safeT @loan.repaymentPlan nestedPath="common.repayment_plans" defaultToBlank=true}}
            {{/if}}
          </p>

          <h2 class="col-span-3 text-right font-semibold">
            {{t "pslf_dashboard.actions.dialog.loan_type"}}:
          </h2>
          <p class="col-span-4">
            {{@loan.loanTypeDisplayName}}
          </p>

          <h2 class="col-span-3 text-right font-semibold">
            {{t "pslf_dashboard.actions.dialog.interest_rate"}}:
          </h2>
          <p class="col-span-4">
            {{@loan.interestRate}}
          </p>

          <h2 class="col-span-3 text-right font-semibold">
            {{t "pslf_dashboard.actions.dialog.current_balance"}}:
          </h2>
          <p class="col-span-3">
            {{#if (gte @loan.currentBalance 0)}}
              {{safeFormatNumber @loan.currentBalance style="currency" currency="USD"}}
            {{/if}}
          </p>
        </div>
      </li>
    {{/if}}
  </template>
}
